import React, { useRef, useCallback, useState, useEffect } from 'react';
import { XYCoord } from 'dnd-core';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import update from 'immutability-helper';

const DRAG_ITEM_TYPE = 'SORTABLE_LIST_ITEM';
interface SortableListProps {
  onListChanged: (newItems: Item[]) => void;
  items: Item[];
}

interface Item {
  id: string;
  content: string;
  className?: string;
}
interface DragItem {
  index: number;
  type: string;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
}

interface SortableListItemProps extends DragItem {
  item: Item;
}

export const SortableList: React.FC<SortableListProps> = ({ onListChanged, items }) => {
  const [_items, setItems] = useState<Item[]>(items);

  useEffect(() => {
    setItems(items);
  }, [items]);
  const moveItem = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragItem = _items[dragIndex];
      const updatedItems = update(_items, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragItem],
        ],
      });
      setItems(updatedItems);
      onListChanged(updatedItems);
    },
    [_items]
  );

  return (
    <>
      {_items.map((item, i) => {
        return <SortableListItem moveItem={moveItem} item={item} index={i} type={DRAG_ITEM_TYPE} />;
      })}
    </>
  );
};

const SortableListItem: React.FC<SortableListItemProps> = ({ index, moveItem, item }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop({
    accept: DRAG_ITEM_TYPE,
    hover(dragItem: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = dragItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current && ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveItem(dragIndex, hoverIndex);
      dragItem.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type: DRAG_ITEM_TYPE, id: item.id, index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={` inline-flex ${item.className}`}
      style={{
        opacity: isDragging ? 0 : 1,
        cursor: 'move',
        display: isDragging ? 'none' : 'initial',
      }}
    >
      {item.content}
    </div>
  );
};
